// src/belediye/pages/profile/ProfilePage.js
//
// Profil Route Page (Theme.css / md-card uyumlu)
// - Üst bar yok
// - 2 eş-boy md-kart yan yana: SOL Profil (büyük görsel + yanında bilgiler),
//   SAĞ Şifre Değiştir (doğrudan, aç/kapat yok)
// - Altında tam-genişlik md-kart: Oturum ve Cihazlar (son 10)
// - Foto modal (varsa büyüt) + inline edit (username/email) + toast
// - CSS: ProfilePage.css + ProfilePage.mobile.css

import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  fetchMyProfile,
  updateMyProfile,
  uploadProfilePhoto,
  deleteProfilePhoto,
  clearProfileErrors,
  fetchMySessions,
  closeOtherSessions,
} from "../../redux/slices/profileSlice";

import PasswordChangeSection from "./PasswordChangeSection";

//   Paylaşılan ev-tarzı bileşeni
import TrDateTime from "../../components/utils/TrDateTime";

import {
  UserCircleIcon,
  CameraIcon,
  TrashIcon,
  PencilSquareIcon,
  CheckIcon,
  XMarkIcon,
  LockClosedIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOffice2Icon,
  ComputerDesktopIcon,
  ClockIcon,
  SignalIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";

import "./ProfilePage.css";
import "./ProfilePage.mobile.css";

const MAX_IMAGE_MB = 10;

function getActiveUnitSlug() {
  try {
    return localStorage.getItem("activeUnitSlug") || "";
  } catch {
    return "";
  }
}

function resolveUnitNameFromMe(meUser) {
  if (!meUser) return "-";
  if (meUser.role === "başkan") return "Başkanlık";

  const activeSlug = getActiveUnitSlug();
  const units = Array.isArray(meUser.units) ? meUser.units : [];
  const found = activeSlug ? units.find((u) => u?.unit_slug === activeSlug) : null;

  if (found?.unit_name) return found.unit_name;

  // Legacy fallback (me.unit object)
  if (typeof meUser.unit === "object" && meUser.unit?.name) return meUser.unit.name;

  return "-";
}

export default function ProfilePage() {
  const dispatch = useDispatch();

  const {
    user,
    loading,
    error,
    updateLoading,
    updateError,
    updateSuccess,
    photoUploading,
    photoUploadError,
    photoDeleteLoading,
    photoDeleteError,
    sessions,
    sessionsLoading,
    sessionsError,
    sessionsClosing,
  } = useSelector((state) => state.profile);

  // -------------------------
  // Init / cleanup
  // -------------------------
  useEffect(() => {
    dispatch(clearProfileErrors());
    dispatch(fetchMyProfile());
    dispatch(fetchMySessions());
    return () => dispatch(clearProfileErrors());
  }, [dispatch]);

  // -------------------------
  // Local UI state
  // -------------------------
  const [editingUsername, setEditingUsername] = useState(false);
  const [username, setUsername] = useState("");

  const [editingEmail, setEditingEmail] = useState(false);
  const [email, setEmail] = useState("");

  // Photo modal
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const modalCloseBtnRef = useRef(null);

  // Toast
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);
  const pushToast = (text, type = "success", ms = 2400) => {
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    setToast({ text, type });
    toastTimerRef.current = setTimeout(() => setToast(null), ms);
  };
  useEffect(() => {
    return () => {
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, []);

  // user değişince inputları doldur
  useEffect(() => {
    if (!user) return;
    setUsername(user.username || "");
    setEmail(user.email || "");
  }, [user]);

  // -------------------------
  // Derived
  // -------------------------
  const profilePhotoUrl = user?.photo || null;

  const fullName = useMemo(() => {
    const n = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();
    return n || "-";
  }, [user]);

  const phoneNumber = useMemo(() => user?.phone_number || "-", [user]);
  const unitName = useMemo(() => resolveUnitNameFromMe(user), [user]);

  const anyBusy = Boolean(loading || updateLoading || photoUploading || photoDeleteLoading);

  // -------------------------
  // Photo upload/delete
  // -------------------------
  const fileInputRef = useRef(null);
  const onPickPhoto = () => fileInputRef.current?.click();

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // aynı dosya tekrar seçilebilsin
    e.target.value = "";

    const sizeMB = file.size / (1024 * 1024);
    if (sizeMB > MAX_IMAGE_MB) {
      pushToast(`Dosya en fazla ${MAX_IMAGE_MB}MB olmalı.`, "error");
      return;
    }
    if (!/^image\//.test(file.type)) {
      pushToast("Lütfen bir resim dosyası seçin.", "error");
      return;
    }

    dispatch(uploadProfilePhoto(file)).then((res) => {
      if (res.meta?.requestStatus === "fulfilled") {
        pushToast("Profil fotoğrafı güncellendi.", "success");
      } else {
        pushToast(res.payload || "Profil fotoğrafı güncellenemedi.", "error");
      }
    });
  };

  const handlePhotoDelete = () => {
    if (!user?.id) return;
    if (!profilePhotoUrl) return;

    const ok = window.confirm("Profil fotoğrafını silmek istiyor musunuz?");
    if (!ok) return;

    dispatch(deleteProfilePhoto()).then((res) => {
      if (res.meta?.requestStatus === "fulfilled") {
        pushToast("Profil fotoğrafı silindi.", "success");
      } else {
        pushToast(res.payload || "Profil fotoğrafı silinemedi.", "error");
      }
    });
  };

  // -------------------------
  // Inline edits
  // -------------------------
  const handleUsernameSave = () => {
    const value = (username || "").trim();
    if (!value || !user?.id) return;

    dispatch(updateMyProfile({ username: value })).then((res) => {
      if (res.meta?.requestStatus === "fulfilled") {
        pushToast("Kullanıcı adı güncellendi.", "success");
        setEditingUsername(false);
      } else {
        pushToast(res.payload || "Kullanıcı adı güncellenemedi.", "error");
      }
    });
  };

  const handleUsernameCancel = () => {
    setUsername(user?.username || "");
    setEditingUsername(false);
  };

  const handleEmailSave = () => {
    const value = (email || "").trim();
    if (!value || !user?.id) return;

    dispatch(updateMyProfile({ email: value })).then((res) => {
      if (res.meta?.requestStatus === "fulfilled") {
        pushToast("E-posta güncellendi.", "success");
        setEditingEmail(false);
      } else {
        pushToast(res.payload || "E-posta güncellenemedi.", "error");
      }
    });
  };

  const handleEmailCancel = () => {
    setEmail(user?.email || "");
    setEditingEmail(false);
  };

  const onUsernameKeyDown = (e) => {
    if (e.key === "Enter") handleUsernameSave();
    if (e.key === "Escape") handleUsernameCancel();
  };

  const onEmailKeyDown = (e) => {
    if (e.key === "Enter") handleEmailSave();
    if (e.key === "Escape") handleEmailCancel();
  };

  // -------------------------
  // Photo modal: esc + focus
  // -------------------------
  useEffect(() => {
    if (!photoModalOpen) return;

    const onKeyDown = (e) => {
      if (e.key === "Escape") setPhotoModalOpen(false);
    };

    document.addEventListener("keydown", onKeyDown);
    setTimeout(() => modalCloseBtnRef.current?.focus(), 0);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, [photoModalOpen]);

  // -------------------------
  // Oturumlar — son 10, diğer cihazlardan çıkış
  // -------------------------
  const sessionList = useMemo(
    () => (Array.isArray(sessions) ? sessions.slice(0, 10) : []),
    [sessions]
  );

  const otherActiveCount = useMemo(
    () => sessionList.filter((s) => s.aktif && !s.bu_cihaz).length,
    [sessionList]
  );

  const handleCloseOthers = () => {
    if (otherActiveCount === 0) return;
    dispatch(closeOtherSessions()).then((res) => {
      if (res.meta?.requestStatus === "fulfilled") {
        pushToast("Diğer oturumlar kapatıldı.", "success");
      } else {
        pushToast(res.payload || "Diğer oturumlar kapatılamadı.", "error");
      }
    });
  };

  // -------------------------
  // Küçük yardımcı: bilgi satırı
  // -------------------------
  const renderInlineRow = ({
    label,
    icon,
    editing,
    setEditing,
    value,
    display,
    onChange,
    onKeyDown,
    onSave,
    onCancel,
    inputType,
    ariaLabel,
  }) => (
    <div className="profile-row">
      <div className="profile-row-k">
        {icon}
        {label}
      </div>
      <div className="profile-row-v">
        {editing ? (
          <div className="profile-inline">
            <input
              className="md-input"
              value={value}
              onChange={onChange}
              onKeyDown={onKeyDown}
              disabled={updateLoading || loading}
              type={inputType || "text"}
              autoFocus
            />
            <div className="profile-inline-btns">
              <button
                type="button"
                className="btn-md btn-md-primary"
                onClick={onSave}
                disabled={updateLoading || loading || !String(value).trim()}
                title="Kaydet"
              >
                <CheckIcon className="pi" />
              </button>
              <button
                type="button"
                className="btn-md btn-md-ghost"
                onClick={onCancel}
                disabled={updateLoading || loading}
                title="İptal"
              >
                <XMarkIcon className="pi" />
              </button>
            </div>
          </div>
        ) : (
          <div className="profile-inline-view">
            <span className="profile-row-value">{display}</span>
            <button
              type="button"
              className="profile-editIconBtn"
              onClick={() => setEditing(true)}
              disabled={loading}
              title="Düzenle"
              aria-label={ariaLabel}
            >
              <PencilSquareIcon className="pi" />
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // -------------------------
  // Render
  // -------------------------
  return (
    <div className="page-container profile-page">
      {/* Global error */}
      {error && <div className="profile-alert profile-alert-error">{error}</div>}

      {/* ÜST: SOL Profil (görsel + yanında bilgiler) | SAĞ Şifre Değiştir */}
      <div className="profile-grid">
        {/* SOL — Profil kartı */}
        <section className="md-card profile-card">
          <div className="profile-card-head">
            <div className="profile-card-headLeft">
              <UserCircleIcon className="pi" />
              Profil Bilgileri
            </div>
          </div>

          <div className="profile-card-body profile-identity-body">
            {/* GÖRSEL — büyük, üzerinde değiştir/sil */}
            <div className="profile-photo">
              <button
                type="button"
                className="profile-photo-frame"
                onClick={() => {
                  if (profilePhotoUrl) setPhotoModalOpen(true);
                  else onPickPhoto();
                }}
                aria-label="Profil fotoğrafı"
                title={profilePhotoUrl ? "Büyüt" : "Fotoğraf yükle"}
              >
                {profilePhotoUrl ? (
                  <img src={profilePhotoUrl} alt="Profil" className="profile-photo-img" />
                ) : (
                  <UserCircleIcon className="profile-photo-fallback" />
                )}
              </button>

              <div className="profile-photo-overlay">
                <button
                  type="button"
                  className="profile-chip"
                  onClick={onPickPhoto}
                  disabled={photoUploading || loading}
                  title="Fotoğraf değiştir"
                >
                  <CameraIcon className="pi" />
                  Değiştir
                </button>
                <button
                  type="button"
                  className="profile-chip profile-chip--danger"
                  onClick={handlePhotoDelete}
                  disabled={!profilePhotoUrl || photoDeleteLoading || loading}
                  title={!profilePhotoUrl ? "Fotoğraf yok" : "Fotoğrafı sil"}
                >
                  <TrashIcon className="pi" />
                  Sil
                </button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handlePhotoChange}
                style={{ display: "none" }}
              />
            </div>

            {/* BİLGİLER — görselin yanında */}
            <div className="profile-info">
              <div className="profile-info-top">
                <div className="profile-info-name">{fullName}</div>
                <span className="md-chip md-chip-info profile-info-unit">
                  <BuildingOffice2Icon className="pi" />
                  {unitName}
                </span>
              </div>

              <div className="profile-rows">
                {renderInlineRow({
                  label: "Kullanıcı adı",
                  icon: null,
                  editing: editingUsername,
                  setEditing: setEditingUsername,
                  value: username,
                  display: `@${user?.username || "-"}`,
                  onChange: (e) => setUsername(e.target.value),
                  onKeyDown: onUsernameKeyDown,
                  onSave: handleUsernameSave,
                  onCancel: handleUsernameCancel,
                  ariaLabel: "Kullanıcı adını düzenle",
                })}

                {renderInlineRow({
                  label: "E-posta",
                  icon: <EnvelopeIcon className="pi" />,
                  editing: editingEmail,
                  setEditing: setEditingEmail,
                  value: email,
                  display: user?.email || "-",
                  onChange: (e) => setEmail(e.target.value),
                  onKeyDown: onEmailKeyDown,
                  onSave: handleEmailSave,
                  onCancel: handleEmailCancel,
                  inputType: "email",
                  ariaLabel: "E-postayı düzenle",
                })}

                {/* Phone (salt-okunur) */}
                <div className="profile-row">
                  <div className="profile-row-k">
                    <PhoneIcon className="pi" />
                    Telefon
                  </div>
                  <div className="profile-row-v">
                    <span className="profile-row-value">{phoneNumber}</span>
                  </div>
                </div>
              </div>

              {(updateError || photoUploadError || photoDeleteError) && (
                <div className="profile-alert profile-alert-error">
                  {updateError || photoUploadError || photoDeleteError}
                </div>
              )}

              {updateSuccess && (
                <div className="profile-alert profile-alert-success">{updateSuccess}</div>
              )}

              {anyBusy && <div className="profile-loading">Yükleniyor...</div>}
            </div>
          </div>
        </section>

        {/* SAĞ — Şifre değiştir kartı (doğrudan, aç/kapat yok) */}
        <section className="md-card profile-card">
          <div className="profile-card-head">
            <div className="profile-card-headLeft">
              <LockClosedIcon className="pi" />
              Şifre Değiştir
            </div>
          </div>

          <div className="profile-card-body">
            <PasswordChangeSection
              onSuccess={() => pushToast("Şifre başarıyla değiştirildi.", "success")}
            />
          </div>
        </section>
      </div>

      {/* OTURUM VE CİHAZLAR — tam genişlik, son 10 */}
      <section className="md-card profile-card profile-sessions-card">
        <div className="profile-card-head profile-card-head--toggle">
          <div className="profile-card-headLeft">
            <ComputerDesktopIcon className="pi" />
            Oturum ve Cihazlar
          </div>
          {otherActiveCount > 0 ? (
            <button
              type="button"
              className="profile-chip profile-chip--danger"
              onClick={handleCloseOthers}
              disabled={sessionsClosing}
              title="Şu anki cihaz hariç diğer oturumları kapat"
            >
              <ArrowRightOnRectangleIcon className="pi" />
              {sessionsClosing ? "Kapatılıyor…" : "Diğer cihazlardan çık"}
            </button>
          ) : null}
        </div>

        <div className="profile-card-body">
          {sessionsLoading ? (
            <div className="profile-loading">Yükleniyor...</div>
          ) : sessionsError ? (
            <div className="profile-alert profile-alert-error">{sessionsError}</div>
          ) : sessionList.length === 0 ? (
            <div className="profile-sessions-empty">Kayıtlı oturum bulunamadı.</div>
          ) : (
            <ul className="profile-sessions">
              {sessionList.map((s) => (
                <li
                  key={s.id}
                  className={`profile-session${s.aktif ? " is-active" : ""}${
                    s.bu_cihaz ? " is-current" : ""
                  }`}
                >
                  <div className="profile-session-icon" aria-hidden="true">
                    <ComputerDesktopIcon />
                  </div>
                  <div className="profile-session-main">
                    <div className="profile-session-top">
                      <span className="profile-session-device">{s.cihaz}</span>
                      {s.bu_cihaz ? (
                        <span className="md-chip md-chip-success profile-session-chip">
                          <SignalIcon className="pi" />
                          Bu cihaz
                        </span>
                      ) : s.aktif ? (
                        <span className="md-chip md-chip-info profile-session-chip">Aktif</span>
                      ) : null}
                    </div>
                    <div className="profile-session-meta">
                      <span className="profile-session-metaItem">
                        <ClockIcon className="pi" />
                        <TrDateTime value={s.giris_zamani} />
                      </span>
                      {s.sure ? (
                        <span className="profile-session-metaItem profile-session-dur">
                          {s.sure}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Photo modal */}
      {photoModalOpen && profilePhotoUrl && (
        <div
          className="profile-modal-overlay"
          onClick={() => setPhotoModalOpen(false)}
          role="dialog"
          aria-modal="true"
        >
          <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
            <button
              ref={modalCloseBtnRef}
              type="button"
              className="profile-modal-close"
              onClick={() => setPhotoModalOpen(false)}
              aria-label="Kapat"
              title="Kapat"
            >
              <XMarkIcon className="pi" />
            </button>

            <div className="profile-modal-title">Profil Fotoğrafı</div>
            <div className="profile-modal-body">
              <img src={profilePhotoUrl} alt="Profil" className="profile-modal-img" />
            </div>

            <div className="profile-modal-actions">
              <button
                type="button"
                className="btn-md btn-md-outline"
                onClick={() => {
                  setPhotoModalOpen(false);
                  onPickPhoto();
                }}
              >
                <CameraIcon className="pi" />
                Değiştir
              </button>

              <button
                type="button"
                className="btn-md btn-md-danger"
                onClick={() => {
                  setPhotoModalOpen(false);
                  handlePhotoDelete();
                }}
                disabled={photoDeleteLoading || loading}
              >
                <TrashIcon className="pi" />
                Sil
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className={`profile-toast ${
            toast.type === "error" ? "profile-toast-error" : "profile-toast-success"
          }`}
          role="status"
          aria-live="polite"
        >
          {toast.text}
        </div>
      )}
    </div>
  );
}
